<template>
    <div class="timeline-container">
        <div class="timeline-wrapper">
            <div class="d-flex align-items-center px-2 py-2">
                <!-- Play Button -->
                <b-button
                    @click="togglePlay"
                    variant="outline-light"
                    size="sm"
                    class="play-button mr-3"
                >
                    <i :class="isPlaying ? 'fa fa-pause' : 'fa fa-play'"></i>
                </b-button>

                <!-- Timeline Track -->
                <div class="flex-grow-1 position-relative">
                    <!-- Popup Highlight -->
                    <div class="timeline-popup" :style="{ left: popupPosition + '%' }">
                        <div class="popup-content">
                            {{ getCurrentTimeLabel() }}
                            <div class="popup-arrow"></div>
                        </div>
                    </div>

                    <div ref="timelineTrack" class="timeline-track" @mousedown="handleMouseDown">
                        <div class="d-flex h-100">
                            <div
                                v-for="(day, index) in getDayMarkers()"
                                :key="`day-${index}`"
                                class="day-marker"
                                :style="{ width: dayWidths[index] + '%' }"
                            >
                                <div class="text-center">
                                    <small class="day-label">{{ day.label }}</small>
                                </div>
                            </div>
                        </div>

                        <div
                            class="progress-indicator"
                            :style="{ left: progressPosition + '%' }"
                        ></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Timeline',
    props: {
        files: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            currentTimeIndex: 0,
            isPlaying: false,
            isDragging: false,
            timeData: [],
            intervalId: null
        };
    },
    computed: {
        maxTimeIndex() {
            return this.timeData.length - 1;
        },
        popupPosition() {
            return this.maxTimeIndex > 0 ? (this.currentTimeIndex / this.maxTimeIndex) * 100 : 0;
        },
        progressPosition() {
            return this.maxTimeIndex > 0 ? (this.currentTimeIndex / this.maxTimeIndex) * 100 : 0;
        },
        dayWidths() {
            const dayMarkers = this.getDayMarkers();
            if (dayMarkers.length === 0) return [];

            const totalFiles = this.timeData.length;
            return dayMarkers.map(dayMarker => {
                const filesInDay = this.timeData.filter(
                    item => item.dayIndex === dayMarker.dayIndex
                ).length;
                return (filesInDay / totalFiles) * 100;
            });
        }
    },
    watch: {
        currentTimeIndex: {
            handler(newIndex, oldIndex) {
                if (newIndex !== oldIndex) {
                    this.emitTimeChange();
                }
            },
            immediate: false
        },
        files: {
            handler(newFiles) {
                if (newFiles && newFiles.length > 0) {
                    this.generateTimeData();
                }
            },
            immediate: true
        }
    },
    mounted() {
        this.generateTimeData();

        document.addEventListener('mousemove', this.handleMouseMove);
        document.addEventListener('mouseup', this.handleMouseUp);

        this.$nextTick(() => {
            this.emitTimeChange();
        });
    },
    beforeDestroy() {
        this.clearInterval();
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.handleMouseUp);
    },
    methods: {
        generateTimeData() {
            if (!this.files || this.files.length === 0) {
                this.timeData = [];
                return;
            }

            const timeData = [];
            const daysOfWeek = [
                'Chủ Nhật',
                'Thứ Hai',
                'Thứ Ba',
                'Thứ Tư',
                'Thứ Năm',
                'Thứ Sáu',
                'Thứ Bảy'
            ];

            const tiffFiles = this.files.filter(filename => filename.endsWith('.tiff'));
            const sortedFiles = [...tiffFiles].sort();

            const uniqueDates = [];
            sortedFiles.forEach(filename => {
                const timeInfo = this.parseFilename(filename);
                if (timeInfo) {
                    const dateStr = timeInfo.date.toDateString();
                    if (!uniqueDates.includes(dateStr)) {
                        uniqueDates.push(dateStr);
                    }
                }
            });

            sortedFiles.forEach((filename, index) => {
                const timeInfo = this.parseFilename(filename);
                if (timeInfo) {
                    const { date, hour, minute } = timeInfo;
                    const dayName = daysOfWeek[date.getDay()];
                    const dayNumber = date.getDate();
                    const monthNumber = (date.getMonth() + 1).toString().padStart(2, '0');

                    const dayIndex = uniqueDates.indexOf(date.toDateString());

                    const isFirstOfDay =
                        timeData.length === 0 ||
                        timeData[timeData.length - 1].dayIndex !== dayIndex;

                    const timeLabel = `${hour
                        .toString()
                        .padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

                    timeData.push({
                        filename: filename,
                        dayIndex: dayIndex,
                        label: `${dayName} ${dayNumber}/${monthNumber}`,
                        timeLabel: timeLabel,
                        isFirstOfDay: isFirstOfDay
                    });
                }
            });

            this.timeData = timeData;
            this.currentTimeIndex = 0;
        },
        parseFilename(filename) {
            const match = filename.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})\.tiff$/);
            if (!match) return null;

            const [, year, month, day, hour, minute] = match;
            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

            return {
                date: date,
                hour: parseInt(hour),
                minute: parseInt(minute)
            };
        },
        togglePlay() {
            this.isPlaying = !this.isPlaying;

            if (this.isPlaying && !this.isDragging) {
                this.startInterval();
            } else {
                this.clearInterval();
            }
        },
        startInterval() {
            this.clearInterval();
            this.intervalId = setInterval(() => {
                if (this.currentTimeIndex >= this.maxTimeIndex) {
                    this.isPlaying = false;
                    this.currentTimeIndex = 0;
                    this.clearInterval();
                } else {
                    this.currentTimeIndex++;
                }
            }, 2000);
        },
        clearInterval() {
            if (this.intervalId) {
                clearInterval(this.intervalId);
                this.intervalId = null;
            }
        },
        handleMouseDown(e) {
            this.isDragging = true;
            this.isPlaying = false;
            this.clearInterval();
            this.updateTimeFromPosition(e.clientX);
        },
        handleMouseMove(e) {
            if (this.isDragging) {
                this.updateTimeFromPosition(e.clientX);
            }
        },
        handleMouseUp() {
            this.isDragging = false;
        },
        updateTimeFromPosition(clientX) {
            const track = this.$refs.timelineTrack;
            if (track) {
                const rect = track.getBoundingClientRect();
                const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
                const exactIndex = percentage * this.maxTimeIndex;
                this.currentTimeIndex = Math.round(exactIndex);
            }
        },
        getCurrentTimeLabel() {
            const currentData = this.timeData[this.currentTimeIndex];
            if (!currentData) return 'Hiện tại';
            return currentData.timeLabel;
        },
        getDayMarkers() {
            return this.timeData.filter(data => data.isFirstOfDay);
        },
        emitTimeChange() {
            const currentData = this.timeData[this.currentTimeIndex];
            if (currentData) {
                this.$emit('time-change', currentData.filename || null);
            }
        }
    }
};
</script>

<style scoped>
.timeline-container {
    display: flex;
    align-items: flex-end;
    position: relative;
    padding-left: 100px;
    padding-right: 100px;
}

.timeline-wrapper {
    position: relative;
    width: 100%;
    border-radius: 10px;
}

.play-button {
    width: 35px;
    height: 35px;
    bottom: 5px;
    position: relative;
    border-radius: 50%;
    background: #707070;
    color: white;
}

.timeline-popup {
    position: absolute;
    top: -40px;
    transform: translateX(-50%);
    z-index: 20;
    transition: all 0.3s ease;
}

.popup-content {
    background: #fd7e14;
    color: white;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    position: relative;
}

.popup-arrow {
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid #fd7e14;
}

.timeline-track {
    position: relative;
    height: 32px;
    background: #707070;
    border-radius: 10px 10px 0px 0px;
    cursor: pointer;
    overflow: hidden;
}

.day-marker {
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    border-right: 1px solid #6c757d;
    color: white;
    transition: all 0.3s ease;
}

.day-marker:last-child {
    border-right: none;
}

.day-marker:hover {
    color: white;
}

.day-label {
    display: block;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}

.progress-indicator {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #fd7e14;
    transition: all 0.3s ease;
    z-index: 10;
}
</style>
